import React from 'react';

import classNames from 'classnames';

import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollarSign, faSign } from '@fortawesome/pro-regular-svg-icons';

import Thumbnail from 'bundles/crm/properties/thumbnail';
import * as addressFormats from 'helpers/formatters/address_formats';
import { isPresent } from 'helpers/string_helpers';

import type { Props as MapListItemProps } from 'bundles/research/index/components/map_list_item/types';
import type { Property } from 'bundles/research/index/properties/types';

interface Comp extends Property {
  address: string;
  buildingSizeLabel?: string;
  propertySubtypeLabel?: string;

  // Sale
  buyerName?: string;
  capRatePct?: number;
  currencyCode?: string;
  price?: number;
  pricePerSf?: number;
  saleDate?: string;

  // Lease
  leaseExecutionDate?: string;
  leaseRateAmountLabel?: string;
  leaseTerm?: number;
  leaseTermUnits?: 'months' | 'years';
  leaseTypeShortName?: string;
  tenantString?: string;
}

interface Props extends Omit<MapListItemProps, 'record'> {
  record: Comp;
}

function formatCurrency(value: number, currency = 'USD', options: Intl.NumberFormatOptions = {}) {
  return new Intl.NumberFormat('en-US', { currency, style: 'currency', ...options }).format(value);
}

// Dates are serialized as YYYY-MM-DD; format in UTC so the month doesn't shift a day back
function monthYear(isoDate?: string) {
  if (!isoDate) return null;

  return new Date(`${isoDate}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    timeZone: 'UTC',
    year: 'numeric',
  });
}

function saleDetails(comp: Comp) {
  return [
    comp.price != null &&
      formatCurrency(comp.price, comp.currencyCode, { maximumFractionDigits: 1, notation: 'compact' }),
    comp.pricePerSf != null &&
      `${formatCurrency(comp.pricePerSf, comp.currencyCode, { maximumFractionDigits: 0 })}/SF`,
    comp.capRatePct != null && `${comp.capRatePct.toFixed(1)}% cap`,
    comp.buyerName,
  ];
}

function leaseDetails(comp: Comp) {
  const termUnit = comp.leaseTermUnits === 'years' ? 'yr' : 'mo';

  return [
    comp.leaseRateAmountLabel,
    comp.leaseTypeShortName,
    comp.leaseTerm != null && `${comp.leaseTerm} ${termUnit}`,
    comp.tenantString,
  ];
}

export default function CompMapListItem({
  googleMapsApiKey,
  isSelected,
  record,
  onClick,
  onSelectionToggle,
  ...props
}: Props) {
  const isSale = record.type === 'Research::Sale';
  const date = monthYear(isSale ? record.saleDate : record.leaseExecutionDate);
  const details = (isSale ? saleDetails(record) : leaseDetails(record)).filter(Boolean).join(' • ');

  return (
    <div className="card clickable" onClick={onClick} {...props}>
      <div className="card-body p-2">
        <div className="d-flex gap-2 align-items-start">
          {/* Keep checkbox clicks from opening the comp */}
          <div
            className="flex-grow-0 flex-shrink-1 pt-1"
            onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()}
          >
            <input
              checked={isSelected}
              className="form-check-input"
              type="checkbox"
              onChange={onSelectionToggle}
            />
          </div>

          <div className="d-flex flex-column gap-4 flex-fill min-w-0 align-self-stretch">
            <div className="flex-fill">
              <h6 className="fs-large fw-semibold">
                {record.displayName || record.address}
              </h6>
              <div className="mb-2 text-muted">{addressFormats.twoLine(record)[1]}</div>
              <div className="d-flex align-items-center gap-2 fs-small">
                <Badge
                  className={classNames('text-body', isSale ? 'bg-success-subtle' : 'bg-info-subtle')}
                >
                  <FontAwesomeIcon icon={isSale ? faDollarSign : faSign} />
                  {[isSale ? 'Sold' : 'Leased', date].filter(Boolean).join(' · ')}
                </Badge>
                <span className="text-truncate">{details}</span>
              </div>
            </div>
            <div className="text-muted fs-small">
              {[record.propertyType, record.propertySubtypeLabel, record.buildingSizeLabel]
                .filter(isPresent)
                .join(' • ')}
            </div>
          </div>

          <div className="flex-grow-0 flex-shrink-0 rounded overflow-hidden property-thumbnail">
            <Thumbnail
              autoHeight
              googleMapsApiKey={googleMapsApiKey}
              height={93}
              property={record}
              width={140}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
